import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components';
import { CustomInput, CustomErrorMessage } from '@/components/common/Form';
import {
  getPatAsync,
  updatePatAsync,
  removePatAsync,
  patSelector,
} from '../redux';
import {
  getPatLoadingSelector,
  updatePatLoadingSelector,
} from '@/redux/loading';

const PatSetting = () => {
  const dispatch = useDispatch();
  const pat = useSelector(patSelector);
  const isPatLoading = useSelector(getPatLoadingSelector);
  const isPatUpdating = useSelector(updatePatLoadingSelector);

  const [isEditing, setIsEditing] = useState(false);
  const [patValue, setPatValue] = useState('');
  const [expiresAtValue, setExpiresAtValue] = useState('');
  const [errors, setErrors] = useState({ pat: null, expiresAt: null });

  useEffect(() => {
    dispatch(getPatAsync.request());
  }, [dispatch]);

  const validateRFC3339Date = (dateString) => {
    if (!dateString) {
      return false;
    }

    // RFC3339 regex pattern
    const rfc3339Regex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;

    if (!rfc3339Regex.test(dateString)) {
      return false;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return false;
    }

    // Check if the date is in the future
    return date > new Date();
  };

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setErrors({ pat: null, expiresAt: null });
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setPatValue('');
    setExpiresAtValue('');
    setErrors({ pat: null, expiresAt: null });
  }, []);

  const handleRemove = useCallback(() => {
    dispatch(removePatAsync.request());
  }, [dispatch]);

  const handleSave = useCallback(() => {
    const newErrors = { pat: null, expiresAt: null };
    let hasErrors = false;

    if (!patValue.trim()) {
      newErrors.pat = 'Personal Access Token is required';
      hasErrors = true;
    }

    if (expiresAtValue.trim() && !validateRFC3339Date(expiresAtValue)) {
      newErrors.expiresAt = 'Expiration date must be a valid RFC3339 formatted date in the future';
      hasErrors = true;
    }

    setErrors(newErrors);

    if (!hasErrors) {
      dispatch(updatePatAsync.request({
        pat: patValue,
        expires_at: expiresAtValue,
      }));
      setIsEditing(false);
    }
  }, [dispatch, patValue, expiresAtValue]);

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-3">Aspyn Personal Access Token</h3>
      <p className="text-sm text-gray-600">
        This token will be stored in the cache and will be reset after each deploy or cache clearing.
      </p>
      <p className="text-sm text-gray-600 mb-4">
        (Clear Cache feature from this page does not affect personal access tokens)
      </p>
      <p className="text-xs text-gray-600 mb-4 italic">
        This feature was developed to enable us to set up personal access tokens for
        Aspyn without requiring assistance from the DevOps team.
      </p>

      {isPatLoading ? (
        <div className="text-sm text-gray-600">Loading token information...</div>
      ) : (
        <>
          {!pat && !isEditing ? (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                No token exists. Please add a new token.
              </p>
            </div>
          ) : null}

          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personal Access Token
                <span className="text-red-500">*</span>
              </label>
              {isEditing ? (
                <>
                  <CustomInput
                    type="text"
                    value={patValue}
                    onChange={(event) => setPatValue(event.target.value)}
                    hasError={!!errors.pat}
                    placeholder="Enter Personal Access Token"
                    className="w-full"
                  />
                  {errors.pat && (
                    <CustomErrorMessage text={errors.pat} />
                  )}
                </>
              ) : (
                <div className="text-sm text-gray-900 break-all">
                  {pat?.token || 'N/A'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expires At
              </label>
              {isEditing ? (
                <>
                  <CustomInput
                    type="text"
                    value={expiresAtValue}
                    onChange={(event) => setExpiresAtValue(event.target.value)}
                    hasError={!!errors.expiresAt}
                    placeholder="YYYY-MM-DDTHH:MM:SSZ (e.g. 2026-12-31T23:59:59Z)"
                    className="w-full"
                  />
                  {errors.expiresAt && (
                    <CustomErrorMessage text={errors.expiresAt} />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Format: RFC3339 (e.g. 2026-12-31T23:59:59+00:00).
                    An empty field means that the token has no expiration date.
                  </p>
                </>
              ) : (
                <div className="text-sm text-gray-900">
                  {pat?.expiresAt || 'N/A'}
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-3">
            {isPatUpdating || isEditing ? (
              <>
                <Button
                  onClick={handleSave}
                  disabled={isPatUpdating}
                  color="blue"
                >
                  {isPatUpdating ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={isPatUpdating}
                  color="gray"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit} color="blue">
                Set New Token To Cache
              </Button>
            )}
            {pat?.token && !isEditing && (
              <Button onClick={handleRemove} disabled={isPatUpdating} color="red">
                Remove Token From Cache
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PatSetting;
